import {
  useReducedMotion,
  motion,
} from "framer-motion";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  getLanguageFromPath,
  getRoutePath,
} from "../site/config/routes";

import Footer from "../template/components/layout/Footer";

import studioHero from "../assets/studio-kuvat/studio-hero.webp";
import studioWide02 from "../assets/studio-kuvat/studio-wide-02.webp";
import studioPortrait01 from "../assets/studio-kuvat/studio-portrait-01.webp";
import studioPortrait02 from "../assets/studio-kuvat/studio-portrait-02.webp";
import studioDetail01 from "../assets/studio-kuvat/studio-detail-01.webp";
import studioDetail02 from "../assets/studio-kuvat/studio-detail-02.webp";
import studioDetail03 from "../assets/studio-kuvat/studio-detail-03.webp";
import studioSession01 from "../assets/studio-kuvat/studio-session-01.webp";
import studioSession02 from "../assets/studio-kuvat/studio-session-02.webp";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  STUDIO PAGE
  ============================================================

  Studio esitellään valokuvakirjamaisena / editorial-kokonaisuutena.

  Huom:
  - nykyiset kuvat ovat template-/demokuvia
  - tekstit ovat demo-sisältöä
  - asiakkaan oikeat kuvat ja tekstit voidaan vaihtaa myöhemmin
  - sivu toimii FI / EN
  - mobiilissa sommittelu pinoutuu luonnollisesti
*/

const studioContent = {
  fi: {
    hero: {
      eyebrow:
        "Studio / kuvausympäristö",

      title:
        "Tila, jossa kuva saa hengittää.",

      description:
        "Rauhallinen kuvausympäristö antaa tilaa ihmiselle, valolle ja hetkelle. Studio voidaan esitellä osana kuvaajan visuaalista maailmaa — ei vain teknisenä tilana.",
    },

    intro: {
      eyebrow:
        "Valo. Tila. Tunnelma.",

      title:
        "Studio on osa kuvauskokemusta.",

      description:
        "Hyvä kuvausympäristö ei vie huomiota kuvattavalta. Sen tehtävä on antaa valolle, liikkeelle ja kohtaamiselle tilaa. Vaalea ja rauhallinen ympäristö toimii pohjana monenlaisille kuvauksille.",
    },

    atmosphere: {
      eyebrow:
        "Editorial space",

      title:
        "Välillä tarvitaan vain valo, yksi hyvä paikka ja aikaa.",

      description:
        "Studion esittely voidaan rakentaa samalla tavalla kuin muu portfolio: suuria kuvia, pieniä yksityiskohtia ja tarkoituksella jätettyä tyhjää tilaa.",
    },

    session: {
      eyebrow:
        "Kuvauksen aikana",

      title:
        "Tila muuttuu, kun ihminen astuu kuvaan.",

      description:
        "Studio ei ole vain tyhjä huone. Kuvauksen aikana tila rakentuu valosta, ohjauksesta, liikkeestä ja kuvattavan omasta olemuksesta.",
    },

    details: {
      eyebrow:
        "Yksityiskohdat",

      title:
        "Rauhallinen kokonaisuus syntyy pienistä asioista.",

      description:
        "Kalusteet, taustat, materiaalit ja luonnollinen valo voivat muodostaa kuvauksen ympärille oman hienovaraisen maailmansa.",
    },

    cta: {
      eyebrow:
        "Suunnitellaan kuvaus",

      title:
        "Millaisen tunnelman sinä haluaisit kuviisi?",

      description:
        "Kuvaus voidaan suunnitella studiolle, miljööseen tai muuhun yhdessä valittuun ympäristöön.",

      button:
        "Ota yhteyttä",
    },

    alt: {
      hero:
        "Vaalea valokuvausstudio suurilla ikkunoilla",

      wide:
        "Vaalea studiotila ja nojatuoli",

      portrait01:
        "Valokuvausstudio ja kuvausvalo",

      portrait02:
        "Vaalea studiotila ja kuvaajan tuoli",

      detail01:
        "Studion tausta, tuoli ja sisustuselementti",

      detail02:
        "Vaalea studiotila ja luonnonvalo",

      detail03:
        "Vaalea sisustuksellinen yksityiskohta",

      session01:
        "Valokuvaaja työskentelemässä studiossa",

      session02:
        "Valokuvaaja kuvaamassa henkilöä studiossa",
    },
  },

  en: {
    hero: {
      eyebrow:
        "Studio / photography space",

      title:
        "A space where the image can breathe.",

      description:
        "A calm photography environment leaves room for the person, the light and the moment. The studio can become part of the photographer's visual world rather than simply a technical space.",
    },

    intro: {
      eyebrow:
        "Light. Space. Atmosphere.",

      title:
        "The studio is part of the experience.",

      description:
        "A good photography environment never competes with the subject. Its role is to leave space for light, movement and genuine interaction.",
    },

    atmosphere: {
      eyebrow:
        "Editorial space",

      title:
        "Sometimes all you need is light, one good place and time.",

      description:
        "The studio can be presented just like the rest of the portfolio: large photographs, quiet details and intentionally generous empty space.",
    },

    session: {
      eyebrow:
        "During the session",

      title:
        "The space changes when someone steps into the frame.",

      description:
        "A studio is more than an empty room. During a session it becomes a combination of light, direction, movement and the personality of the person being photographed.",
    },

    details: {
      eyebrow:
        "The details",

      title:
        "A quiet atmosphere is built from small things.",

      description:
        "Furniture, backgrounds, materials and natural light can create a subtle visual world around the session.",
    },

    cta: {
      eyebrow:
        "Plan your session",

      title:
        "What kind of atmosphere would you like for your photographs?",

      description:
        "A session can be planned for the studio, outdoors or another environment selected together.",

      button:
        "Get in touch",
    },

    alt: {
      hero:
        "Bright photography studio with large windows",

      wide:
        "Bright studio space with an armchair",

      portrait01:
        "Photography studio with studio light",

      portrait02:
        "Bright studio space with a photographer chair",

      detail01:
        "Studio backdrop, chair and interior detail",

      detail02:
        "Bright studio interior with natural light",

      detail03:
        "Minimal interior detail in neutral tones",

      session01:
        "Photographer working in a studio",

      session02:
        "Photographer photographing a person in a studio",
    },
  },
};

/*
  ============================================================
  REVEAL WRAPPER
  ============================================================

  Yhteinen erittäin kevyt reveal.
  Reduced motion huomioidaan käyttäjän asetusten mukaan.
*/

function Reveal({
  children,
  className = "",
  delay = 0,
  amount = 0.15,
}) {
  const reduceMotion =
    useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              y: 28,
            }
      }
      whileInView={
        reduceMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
            }
      }
      viewport={{
        once: true,
        amount,
      }}
      transition={{
        duration: 0.9,
        delay,
        ease: [
          0.22,
          1,
          0.36,
          1,
        ],
      }}
    >
      {children}
    </motion.div>
  );
}

/*
  ============================================================
  PAGE
  ============================================================
*/

export default function StudioPage() {
  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  const content =
    studioContent[language] ||
    studioContent.fi;

  return (
    <>
      <article
        className="
          overflow-hidden
          bg-canvas
          text-text
        "
      >
        {/* ======================================================
            HERO
            ====================================================== */}

        <section
          className="
            border-b
            border-border
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-editorial
              px-6
              pb-16
              pt-16

              sm:px-10
              sm:pb-24
              sm:pt-20

              lg:px-16
              lg:pb-32
              lg:pt-28
            "
          >
            <Reveal
              className="
                grid
                gap-10

                lg:grid-cols-12
                lg:items-end
                lg:gap-8
              "
              amount={0.1}
            >
              <div
                className="
                  lg:col-span-8
                "
              >
                <p
                  className="
                    mb-7
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.3em]
                    text-muted

                    sm:text-[10px]
                  "
                >
                  01 /{" "}
                  {content.hero.eyebrow}
                </p>

                <h1
                  className="
                    m-0
                    max-w-5xl
                    font-display
                    text-[clamp(4.2rem,10vw,10rem)]
                    font-normal
                    leading-[0.82]
                    tracking-[-0.06em]
                  "
                >
                  Studio
                </h1>
              </div>

              <div
                className="
                  lg:col-span-4
                  lg:pb-2
                "
              >
                <div
                  className="
                    mb-7
                    h-px
                    w-10
                    bg-text
                  "
                  aria-hidden="true"
                />

                <h2
                  className="
                    m-0
                    max-w-md
                    font-display
                    text-[clamp(2rem,3vw,3.2rem)]
                    font-normal
                    leading-[0.95]
                    tracking-[-0.04em]
                  "
                >
                  {content.hero.title}
                </h2>

                <p
                  className="
                    mb-0
                    mt-7
                    max-w-md
                    text-sm
                    leading-7
                    text-muted

                    sm:text-base
                    sm:leading-8
                  "
                >
                  {content.hero.description}
                </p>
              </div>
            </Reveal>

            <Reveal
              className="
                mt-14

                sm:mt-20
                lg:mt-28
              "
              delay={0.08}
              amount={0.08}
            >
              <div
                className="
                  relative
                  aspect-[4/3]
                  overflow-hidden
                  bg-surface

                  sm:aspect-[16/10]
                  lg:aspect-[16/8]
                "
              >
                <img
                  src={studioHero}
                  alt={content.alt.hero}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="
                    h-full
                    w-full
                    object-cover
                  "
                />

                <div
                  className="
                    absolute
                    bottom-5
                    left-5
                    border
                    border-white/45
                    bg-black/10
                    px-4
                    py-2
                    text-[8px]
                    uppercase
                    tracking-[0.25em]
                    text-white
                    backdrop-blur-sm

                    sm:bottom-7
                    sm:left-7
                    sm:text-[9px]
                  "
                >
                  Photography studio
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ======================================================
            INTRO / BOOK SPREAD
            ====================================================== */}

        <section
          className="
            border-b
            border-border
            bg-surface
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-editorial
              px-6
              pb-16
              pt-20

              sm:px-10
              sm:py-32

              lg:px-16
              lg:py-40
            "
          >
            <div
              className="
                grid
                gap-12

                sm:gap-16

                lg:grid-cols-12
                lg:gap-x-8
                lg:gap-y-0
              "
            >
              {/* TEXT PAGE */}

              <Reveal
                className="
                  min-w-0

                  lg:col-span-6
                  lg:pt-14
                "
                amount={0.12}
              >
                <p
                  className="
                    mb-8
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.28em]
                    text-muted

                    sm:text-[10px]
                  "
                >
                  02 /{" "}
                  {content.intro.eyebrow}
                </p>

                <h2
                  className="
                    m-0
                    max-w-[9em]
                    font-display
                    text-[2.7rem]
                    font-normal
                    leading-[0.92]
                    tracking-[-0.045em]

                    sm:text-[clamp(3.2rem,5.4vw,4.5rem)]
                    sm:leading-[0.9]
                    sm:tracking-[-0.05em]

                    lg:max-w-none
                  "
                >
                  {content.intro.title}
                </h2>

                <div
                  className="
                    my-10
                    h-px
                    w-10
                    bg-text
                  "
                  aria-hidden="true"
                />

                <p
                  className="
                    m-0
                    max-w-md
                    text-sm
                    leading-7
                    text-muted

                    sm:text-base
                    sm:leading-8
                  "
                >
                  {content.intro.description}
                </p>
              </Reveal>

              {/* IMAGE PAGE */}

              <Reveal
                className="
                  min-w-0

                  lg:col-span-5
                  lg:col-start-8
                "
                delay={0.1}
                amount={0.1}
              >
                <div
                  className="
                    relative
                    aspect-[4/3]
                    overflow-hidden
                    bg-canvas

                    sm:aspect-[4/5]
                  "
                >
                  <img
                    src={studioPortrait01}
                    alt={content.alt.portrait01}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                      object-center
                    "
                  />
                </div>

                <div
                  className="
                    mt-5
                    flex
                    flex-wrap
                    items-center
                    justify-between
                    gap-x-6
                    gap-y-3
                    border-t
                    border-border
                    pt-4

                    sm:flex-nowrap
                  "
                >
                  <span
                    className="
                      shrink-0
                      text-[8px]
                      uppercase
                      tracking-[0.24em]
                      text-muted

                      sm:text-[9px]
                    "
                  >
                    Studio / 02
                  </span>

                  <span
                    className="
                      hidden
                      text-[8px]
                      uppercase
                      tracking-[0.2em]
                      text-muted

                      sm:inline
                      sm:text-[9px]
                    "
                  >
                    Light &amp; space
                  </span>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ======================================================
            BOOK SPREAD / ATMOSPHERE
            ====================================================== */}

        <section
          className="
            border-b
            border-border
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-editorial
              px-6
              py-20

              sm:px-10
              sm:py-32

              lg:px-16
              lg:py-40
            "
          >
            <Reveal
              className="
                mb-16
                max-w-4xl

                lg:mb-24
              "
            >
              <p
                className="
                  mb-6
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[0.28em]
                  text-muted

                  sm:text-[10px]
                "
              >
                03 /{" "}
                {content.atmosphere.eyebrow}
              </p>

              <h2
                className="
                  m-0
                  font-display
                  text-[2.7rem]
                  font-normal
                  leading-[0.92]
                  tracking-[-0.045em]

                  sm:text-[clamp(3rem,6vw,6.5rem)]
                  sm:leading-[0.9]
                  sm:tracking-[-0.05em]
                "
              >
                {content.atmosphere.title}
              </h2>
            </Reveal>

            <div
              className="
                grid
                gap-10

                lg:grid-cols-12
                lg:items-start
                lg:gap-8
              "
            >
              <Reveal
                className="
                  lg:col-span-8
                "
                amount={0.08}
              >
                <div
                  className="
                    aspect-[4/3]
                    overflow-hidden
                    bg-surface

                    sm:aspect-[16/10]
                  "
                >
                  <img
                    src={studioWide02}
                    alt={content.alt.wide}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                </div>
              </Reveal>

              <Reveal
                className="
                  lg:col-span-3
                  lg:col-start-10
                  lg:mt-28
                "
                delay={0.1}
              >
                <div
                  className="
                    aspect-[3/2]
                    overflow-hidden
                    bg-surface

                    sm:aspect-[4/5]
                  "
                >
                  <img
                    src={studioDetail02}
                    alt={content.alt.detail02}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                </div>

                <p
                  className="
                    mb-0
                    mt-7
                    text-sm
                    leading-7
                    text-muted
                  "
                >
                  {content.atmosphere.description}
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ======================================================
            SESSION
            ====================================================== */}

        <section
          className="
            border-b
            border-border
            bg-surface
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-editorial
              px-6
              py-20

              sm:px-10
              sm:py-32

              lg:px-16
              lg:py-40
            "
          >
            <Reveal
              className="
                grid
                gap-10

                lg:grid-cols-12
                lg:items-end
                lg:gap-8
              "
            >
              <div
                className="
                  lg:col-span-7
                "
              >
                <p
                  className="
                    mb-6
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.28em]
                    text-muted

                    sm:text-[10px]
                  "
                >
                  04 /{" "}
                  {content.session.eyebrow}
                </p>

                <h2
                  className="
                    m-0
                    font-display
                    text-[2.7rem]
                    font-normal
                    leading-[0.92]
                    tracking-[-0.045em]

                    sm:text-[clamp(3rem,6vw,6rem)]
                    sm:leading-[0.9]
                    sm:tracking-[-0.05em]
                  "
                >
                  {content.session.title}
                </h2>
              </div>

              <p
                className="
                  m-0
                  max-w-md
                  text-sm
                  leading-7
                  text-muted

                  sm:text-base
                  sm:leading-8

                  lg:col-span-4
                  lg:col-start-9
                "
              >
                {content.session.description}
              </p>
            </Reveal>

            <div
              className="
                mt-12
                grid
                gap-8

                sm:mt-20
                sm:gap-10

                lg:mt-28
                lg:grid-cols-12
                lg:items-start
                lg:gap-8
              "
            >
              <Reveal
                className="
                  lg:col-span-5
                "
                amount={0.08}
              >
                <div
                  className="
                    aspect-[4/3]
                    overflow-hidden
                    bg-canvas

                    sm:aspect-[4/5]
                  "
                >
                  <img
                    src={studioSession01}
                    alt={content.alt.session01}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                </div>
              </Reveal>

              <Reveal
                className="
                  lg:col-span-7
                  lg:mt-32
                "
                delay={0.1}
                amount={0.08}
              >
                <div
                  className="
                    aspect-[4/3]
                    overflow-hidden
                    bg-canvas
                  "
                >
                  <img
                    src={studioSession02}
                    alt={content.alt.session02}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ======================================================
            DETAILS
            ====================================================== */}

        <section
          className="
            border-b
            border-border
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-editorial
              px-6
              py-20

              sm:px-10
              sm:py-32

              lg:px-16
              lg:py-40
            "
          >
            <Reveal
              className="
                grid
                gap-12

                lg:grid-cols-12
                lg:items-center
                lg:gap-8
              "
            >
              <div
                className="
                  lg:col-span-5
                "
              >
                <p
                  className="
                    mb-6
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.28em]
                    text-muted

                    sm:text-[10px]
                  "
                >
                  05 /{" "}
                  {content.details.eyebrow}
                </p>

                <h2
                  className="
                    m-0
                    max-w-xl
                    font-display
                    text-[2.7rem]
                    font-normal
                    leading-[0.92]
                    tracking-[-0.045em]

                    sm:text-[clamp(3rem,5.5vw,5.5rem)]
                    sm:leading-[0.9]
                    sm:tracking-[-0.05em]
                  "
                >
                  {content.details.title}
                </h2>

                <p
                  className="
                    mb-0
                    mt-8
                    max-w-md
                    text-sm
                    leading-7
                    text-muted

                    sm:text-base
                    sm:leading-8
                  "
                >
                  {content.details.description}
                </p>
              </div>

              <div
                className="
                  grid
                  grid-cols-2
                  gap-4

                  sm:gap-6

                  lg:col-span-6
                  lg:col-start-7
                  lg:gap-8
                "
              >
                <Reveal>
                  <div
                    className="
                      aspect-[4/5]
                      overflow-hidden
                      bg-surface
                    "
                  >
                    <img
                      src={studioDetail01}
                      alt={content.alt.detail01}
                      loading="lazy"
                      decoding="async"
                      className="
                        h-full
                        w-full
                        object-cover
                      "
                    />
                  </div>
                </Reveal>

                <Reveal
                  className="
                    mt-16

                    sm:mt-24
                  "
                  delay={0.08}
                >
                  <div
                    className="
                      aspect-[4/5]
                      overflow-hidden
                      bg-surface
                    "
                  >
                    <img
                      src={studioDetail03}
                      alt={content.alt.detail03}
                      loading="lazy"
                      decoding="async"
                      className="
                        h-full
                        w-full
                        object-cover
                      "
                    />
                  </div>
                </Reveal>
              </div>
            </Reveal>

            <Reveal
              className="
                mt-20
                grid
                gap-10

                sm:mt-28

                lg:mt-40
                lg:grid-cols-12
                lg:items-start
                lg:gap-8
              "
              amount={0.08}
            >
              <div
                className="
                  lg:col-span-4
                  lg:col-start-2
                "
              >
                <div
                  className="
                    aspect-[4/5]
                    overflow-hidden
                    bg-surface
                  "
                >
                  <img
                    src={studioPortrait02}
                    alt={content.alt.portrait02}
                    loading="lazy"
                    decoding="async"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                </div>
              </div>

              <div
                className="
                  lg:col-span-5
                  lg:col-start-7
                  lg:mt-32
                "
              >
                <p
                  className="
                    m-0
                    font-display
                    text-[clamp(2.3rem,4vw,4.5rem)]
                    font-normal
                    leading-[0.95]
                    tracking-[-0.045em]
                  "
                >
                  {language === "en"
                    ? "The space stays quiet so the photograph can speak."
                    : "Tila saa olla hiljainen, jotta kuva voi puhua."}
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ======================================================
            CTA
            ====================================================== */}

        <section
          className="
            bg-text
            text-canvas
          "
        >
          <div
            className="
              mx-auto
              grid
              w-full
              max-w-editorial
              gap-12
              px-6
              py-20

              sm:px-10
              sm:py-32

              lg:grid-cols-12
              lg:items-end
              lg:gap-8
              lg:px-16
              lg:py-40
            "
          >
            <Reveal
              className="
                lg:col-span-8
              "
            >
              <p
                className="
                  mb-6
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[0.28em]
                  text-canvas/60

                  sm:text-[10px]
                "
              >
                06 /{" "}
                {content.cta.eyebrow}
              </p>

              <h2
                className="
                  m-0
                  max-w-5xl
                  font-display
                  text-[3rem]
                  font-normal
                  leading-[0.9]
                  tracking-[-0.05em]

                  sm:text-[clamp(3.5rem,7vw,7.5rem)]
                  sm:leading-[0.86]
                  sm:tracking-[-0.055em]
                "
              >
                {content.cta.title}
              </h2>
            </Reveal>

            <Reveal
              className="
                lg:col-span-3
                lg:col-start-10
              "
              delay={0.08}
            >
              <p
                className="
                  m-0
                  text-sm
                  leading-7
                  text-canvas/70

                  sm:text-base
                  sm:leading-8
                "
              >
                {content.cta.description}
              </p>

              <Link
                to={getRoutePath(
                  "contact",
                  language
                )}
                className="
                  group
                  mt-9
                  inline-flex
                  items-center
                  gap-4
                  border-b
                  border-canvas
                  pb-2
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.2em]
                  text-canvas
                  no-underline
                "
              >
                {content.cta.button}

                <span
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </Reveal>
          </div>
        </section>
      </article>

      <Footer
        language={language}
      />
    </>
  );
}